import { tt } from '@agos/shared'
import { IconButton, cn } from '@agos/ui'
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { moveBy, moveTo } from './upsell-rules'

/**
 * A list reordered by drag and drop, with up and down buttons beside every row - `FRONTEND.md` §9.711.
 *
 * **HTML5 drag and drop rather than a library**, because the repository has none and one list does not justify
 * adding one. Its known gap is the reason for the buttons: native drag does not fire from a touch screen or a
 * keyboard, and a till is a tablet. So the buttons are not decoration - on a tablet they are the only way.
 *
 * **Every move answers the whole new order** through `onReorder`, which the caller saves as one `PUT`; the list
 * never holds an order of its own, so what is drawn is always what the caller last accepted.
 */
export function ReorderList<T>({
  entries,
  keyOf,
  marker,
  disabled,
  onReorder,
  children,
}: {
  entries: readonly T[]
  keyOf: (entry: T) => number
  /** `data-{marker}-row`, `-up`, `-down` - what the e2e selects by. */
  marker: string
  /** True while a save is in flight: a second move would race the first `PUT`. */
  disabled: boolean
  onReorder: (next: T[]) => void
  children: (entry: T, index: number) => ReactNode
}) {
  const [dragging, setDragging] = useState<number | null>(null)
  const [over, setOver] = useState<number | null>(null)

  const drop = (to: number) => {
    if (dragging !== null && dragging !== to) onReorder(moveTo(entries, dragging, to))
    setDragging(null)
    setOver(null)
  }

  return (
    <ul className="space-y-1.5">
      {entries.map((entry, index) => {
        const key = keyOf(entry)
        return (
          <li
            key={key}
            {...{ [`data-${marker}-row`]: key }}
            draggable={!disabled}
            onDragStart={(event) => {
              setDragging(index)
              event.dataTransfer.effectAllowed = 'move'
              // Firefox starts no drag without data.
              event.dataTransfer.setData('text/plain', String(key))
            }}
            onDragOver={(event) => {
              if (dragging === null) return
              event.preventDefault()
              event.dataTransfer.dropEffect = 'move'
              setOver(index)
            }}
            onDrop={(event) => {
              event.preventDefault()
              drop(index)
            }}
            onDragEnd={() => {
              setDragging(null)
              setOver(null)
            }}
            className={cn(
              'flex items-center gap-2 rounded-xl border bg-card px-2 py-1.5',
              over === index && dragging !== null && dragging !== index
                ? 'border-primary ring-2 ring-primary/20'
                : 'border-border',
              dragging === index && 'opacity-50',
            )}
          >
            <GripVertical
              aria-hidden
              className={cn(
                'w-4 h-4 shrink-0 text-muted-foreground',
                disabled ? 'opacity-40' : 'cursor-grab',
              )}
            />
            <div className="flex-1 min-w-0">{children(entry, index)}</div>
            <IconButton
              label={tt('Posunúť vyššie')}
              disabled={disabled || index === 0}
              onClick={() => onReorder(moveBy(entries, index, -1))}
              attributes={{ [`data-${marker}-up`]: key }}
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </IconButton>
            <IconButton
              label={tt('Posunúť nižšie')}
              disabled={disabled || index === entries.length - 1}
              onClick={() => onReorder(moveBy(entries, index, 1))}
              attributes={{ [`data-${marker}-down`]: key }}
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </IconButton>
          </li>
        )
      })}
    </ul>
  )
}
